import type { Box, BoxHandle, SnapGuides } from './cropAdjustBoxes';

type Bounds = { width: number; height: number };
type Corner = Exclude<BoxHandle, 'move'>;

const MIN = 24;
export const SNAP_ENTER_PX = 8;
export const SNAP_RELEASE_PX = 20;

export interface StickyAxis {
  target: number;
  offset: number;
}

export type StickyState = { x: StickyAxis | null; y: StickyAxis | null };

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function resizeLocked(start: Box, handle: Corner, dx: number, bounds: Bounds, ratio: number): Box {
  const growLeft = handle === 'nw' || handle === 'sw';
  const growUp = handle === 'nw' || handle === 'ne';
  const anchorX = growLeft ? start.left + start.width : start.left;
  const anchorY = growUp ? start.top + start.height : start.top;

  let width = Math.max(MIN, growLeft ? start.width - dx : start.width + dx);
  let height = width / ratio;
  const maxWidth = growLeft ? anchorX : bounds.width - anchorX;
  const maxHeight = growUp ? anchorY : bounds.height - anchorY;
  if (width > maxWidth) {
    width = maxWidth;
    height = width / ratio;
  }
  if (height > maxHeight) {
    height = maxHeight;
    width = height * ratio;
  }

  return {
    left: growLeft ? anchorX - width : anchorX,
    top: growUp ? anchorY - height : anchorY,
    width,
    height,
  };
}

export function applyDrag(
  start: Box,
  handle: BoxHandle,
  dx: number,
  dy: number,
  bounds: Bounds,
  ratio?: number,
): Box {
  if (handle === 'move') {
    return {
      ...start,
      left: clamp(start.left + dx, 0, bounds.width - start.width),
      top: clamp(start.top + dy, 0, bounds.height - start.height),
    };
  }
  if (ratio) return resizeLocked(start, handle, dx, bounds, ratio);
  let { left, top, width, height } = start;
  if (handle === 'nw' || handle === 'sw') {
    left = clamp(start.left + dx, 0, start.left + start.width - MIN);
    width = start.left + start.width - left;
  }
  if (handle === 'ne' || handle === 'se') {
    width = clamp(start.width + dx, MIN, bounds.width - start.left);
  }
  if (handle === 'nw' || handle === 'ne') {
    top = clamp(start.top + dy, 0, start.top + start.height - MIN);
    height = start.top + start.height - top;
  }
  if (handle === 'sw' || handle === 'se') {
    height = clamp(start.height + dy, MIN, bounds.height - start.top);
  }
  return { left, top, width, height };
}

export function fitBounds(box: Box, bounds: Bounds): Box {
  const width = clamp(box.width, MIN, bounds.width);
  const height = clamp(box.height, MIN, bounds.height);
  return {
    width,
    height,
    left: clamp(box.left, 0, bounds.width - width),
    top: clamp(box.top, 0, bounds.height - height),
  };
}

export function snapTargets(otherBoxes: Box[], bounds: Bounds): { xs: number[]; ys: number[] } {
  const xs = [0, bounds.width / 2, bounds.width];
  const ys = [0, bounds.height / 2, bounds.height];
  for (const b of otherBoxes) {
    xs.push(b.left, b.left + b.width / 2, b.left + b.width);
    ys.push(b.top, b.top + b.height / 2, b.top + b.height);
  }
  return { xs, ys };
}

function closest(value: number, targets: number[], threshold: number): number | null {
  let best: number | null = null;
  let bestDist = threshold;
  for (const t of targets) {
    const dist = Math.abs(t - value);
    if (dist <= bestDist) {
      best = t;
      bestDist = dist;
    }
  }
  return best;
}

function snapAxis(
  pos: number,
  size: number,
  targets: number[],
  enterThreshold: number,
  releaseThreshold: number,
  sticky: StickyAxis | null,
): { pos: number; guide: number | null; sticky: StickyAxis | null } {
  if (sticky) {
    const ref = pos + sticky.offset;
    if (Math.abs(ref - sticky.target) <= releaseThreshold) {
      return { pos: sticky.target - sticky.offset, guide: sticky.target, sticky };
    }
  }
  const candidates = [
    { ref: pos, offset: 0 },
    { ref: pos + size / 2, offset: size / 2 },
    { ref: pos + size, offset: size },
  ];
  let best: { pos: number; guide: number; dist: number; offset: number } | null = null;
  for (const c of candidates) {
    const target = closest(c.ref, targets, enterThreshold);
    if (target === null) continue;
    const dist = Math.abs(target - c.ref);
    if (!best || dist < best.dist) {
      best = { pos: target - c.offset, guide: target, dist, offset: c.offset };
    }
  }
  return best
    ? { pos: best.pos, guide: best.guide, sticky: { target: best.guide, offset: best.offset } }
    : { pos, guide: null, sticky: null };
}

function snapEdge(
  value: number,
  targets: number[],
  enterThreshold: number,
  releaseThreshold: number,
  sticky: StickyAxis | null,
): { value: number; guide: number | null; sticky: StickyAxis | null } {
  if (sticky && Math.abs(value - sticky.target) <= releaseThreshold) {
    return { value: sticky.target, guide: sticky.target, sticky };
  }
  const target = closest(value, targets, enterThreshold);
  return target !== null
    ? { value: target, guide: target, sticky: { target, offset: 0 } }
    : { value, guide: null, sticky: null };
}

export function snapBox(
  box: Box,
  handle: BoxHandle,
  targets: { xs: number[]; ys: number[] },
  enterThreshold: number,
  releaseThreshold: number,
  stickyIn: StickyState,
): { box: Box; guides: SnapGuides; sticky: StickyState } {
  let { left, top, width, height } = box;

  if (handle === 'move') {
    const x = snapAxis(left, width, targets.xs, enterThreshold, releaseThreshold, stickyIn.x);
    const y = snapAxis(top, height, targets.ys, enterThreshold, releaseThreshold, stickyIn.y);
    return {
      box: { left: x.pos, top: y.pos, width, height },
      guides: { x: x.guide, y: y.guide },
      sticky: { x: x.sticky, y: y.sticky },
    };
  }

  const leftEdge = handle === 'nw' || handle === 'sw';
  const topEdge = handle === 'nw' || handle === 'ne';
  const xResult = snapEdge(
    leftEdge ? left : left + width,
    targets.xs,
    enterThreshold,
    releaseThreshold,
    stickyIn.x,
  );
  if (leftEdge) {
    width += left - xResult.value;
    left = xResult.value;
  } else {
    width = xResult.value - left;
  }

  const yResult = snapEdge(
    topEdge ? top : top + height,
    targets.ys,
    enterThreshold,
    releaseThreshold,
    stickyIn.y,
  );
  if (topEdge) {
    height += top - yResult.value;
    top = yResult.value;
  } else {
    height = yResult.value - top;
  }

  return {
    box: { left, top, width, height },
    guides: { x: xResult.guide, y: yResult.guide },
    sticky: { x: xResult.sticky, y: yResult.sticky },
  };
}

export function lockedAxisDelta(
  dx: number,
  dy: number,
  deadzone: number,
  current: 'x' | 'y' | null,
): { dx: number; dy: number; axis: 'x' | 'y' | null } {
  let axis = current;
  if (!axis && (Math.abs(dx) > deadzone || Math.abs(dy) > deadzone)) {
    axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
  }
  if (axis === 'x') return { dx, dy: 0, axis };
  if (axis === 'y') return { dx: 0, dy, axis };
  return { dx, dy, axis };
}
